if (!customElements.get('nexo-archive')) {
  class NexoArchive extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.overview = this.querySelector('.nxa__overview');
      this.covers = this.querySelector('.nxa__covers');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.listen(this, 'click', event => this.handleClick(event));
      if (this.dataset.mode !== 'gallery') return;
      this.session = this.querySelector('.nxa__session');
      this.photos = this.querySelector('.nxa__photos');
      this.status = this.querySelector('.nxa__status');
      this.loadingText = this.status.textContent;
      this.dialog = this.querySelector('dialog');
      this.listen(window, 'popstate', () => this.route(false));
      this.listen(this.dialog, 'keydown', event => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.showFull(this.activeIndex - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.showFull(this.activeIndex + 1); }
      });
      this.listen(this.dialog, 'close', () => {
        document.documentElement.style.overflow = this.previousOverflow || '';
        this.dialog.querySelector('img').removeAttribute('src');
        this.opener?.focus({preventScroll: true});
      });
      this.listen(this.dialog, 'pointerdown', event => { this.pointerStart = {x: event.clientX, y: event.clientY}; });
      this.listen(this.dialog, 'pointerup', event => {
        if (!this.pointerStart || event.pointerType === 'mouse') return;
        const dx = event.clientX - this.pointerStart.x;
        const dy = event.clientY - this.pointerStart.y;
        if (Math.abs(dx) > 65 && Math.abs(dx) > Math.abs(dy) * 1.5) this.showFull(this.activeIndex + (dx < 0 ? 1 : -1));
        this.pointerStart = null;
      });
      this.route(false, false);
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      this.controller = null;
    }
    listen(target, type, callback) { target.addEventListener(type, callback, {signal: this.signal}); }
    handleClick(event) {
      const filter = event.target.closest('[data-filter]');
      if (filter) {
        this.querySelectorAll('[data-filter]').forEach(button => button.setAttribute('aria-pressed', String(button === filter)));
        this.querySelectorAll('.nxa-card').forEach(card => { card.hidden = filter.dataset.filter !== 'all' && card.dataset.kind !== filter.dataset.filter; });
        return;
      }
      const slide = event.target.closest('[data-slide]');
      if (slide) { this.covers.scrollBy({left: Number(slide.dataset.slide) * this.covers.clientWidth * .8, behavior: this.reducedMotion ? 'instant' : 'smooth'}); return; }
      if (this.dataset.mode !== 'gallery') return;
      const card = event.target.closest('.nxa-card__link');
      if (card && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
        event.preventDefault();
        history.pushState({}, '', card.href);
        this.route(true);
        return;
      }
      if (event.target.closest('.nxa__back')) {
        const url = new URL(location.href); url.searchParams.delete('sesion');
        history.pushState({}, '', url); this.route(true); return;
      }
      if (event.target.closest('.nxa__more')) { this.renderBatch(); return; }
      const photo = event.target.closest('[data-photo]');
      if (photo) { this.opener = photo; this.previousOverflow = document.documentElement.style.overflow; this.showFull(Number(photo.dataset.photo)); this.dialog.showModal(); document.documentElement.style.overflow = 'hidden'; return; }
      if (event.target.closest('.nxa__close')) this.dialog.close();
      if (event.target.closest('.nxa__prev')) this.showFull(this.activeIndex - 1);
      if (event.target.closest('.nxa__next')) this.showFull(this.activeIndex + 1);
    }
    async manifest() {
      if (!this.manifestPromise) this.manifestPromise = fetch(this.dataset.manifest, {signal: this.signal}).then(response => {
        if (!response.ok) throw new Error('Archive unavailable');
        return response.json();
      }).catch(error => { this.manifestPromise = null; throw error; });
      return this.manifestPromise;
    }
    hydrateCovers() {
      this.covers.querySelectorAll('template[data-nxa-cover-images]').forEach(template => {
        template.replaceWith(template.content.cloneNode(true));
      });
    }
    async route(focus, animate = true) {
      const slug = new URL(location.href).searchParams.get('sesion');
      this.routeToken = (this.routeToken || 0) + 1;
      const token = this.routeToken;
      if (this.dialog.open) this.dialog.close();
      if (!slug) {
        await this.transition(() => {
          if (token !== this.routeToken) return;
          this.status.hidden = true;
          this.removeAttribute('aria-busy');
          this.session.hidden = true;
          this.overview.hidden = false;
          this.hydrateCovers();
        }, animate);
        if (token !== this.routeToken) return;
        if (focus) {
          this.scrollIntoView({ block: 'start', behavior: 'instant' });
          this.querySelector('.nxa__filter')?.focus({ preventScroll: true });
        }
        return;
      }
      this.status.textContent = this.loadingText;
      this.status.hidden = false;
      this.setAttribute('aria-busy', 'true');
      try {
        const data = await this.manifest();
        if (token !== this.routeToken) return;
        const collection = data.collections.find(item => item.slug === slug);
        if (!collection) throw new Error('Collection unavailable');
        await this.transition(() => {
          if (token !== this.routeToken) return;
          this.activeCollection = collection;
          this.session.querySelector('.nxa__title').textContent = collection.title;
          this.session.querySelector('.nxa__description').textContent = collection.description || '';
          this.session.querySelector('.nxa__count').textContent = `${collection.photos.length} ${this.dataset.photoLabel}`;
          this.photos.replaceChildren();
          this.rendered = 0;
          this.renderBatch();
          this.status.hidden = true;
          this.removeAttribute('aria-busy');
          this.session.hidden = false;
          this.overview.hidden = true;
        }, animate);
        if (token !== this.routeToken) return;
        if (focus) {
          this.scrollIntoView({ block: 'start', behavior: 'instant' });
          this.session.querySelector('h1').focus({ preventScroll: true });
        }
      } catch (error) {
        if (error.name === 'AbortError' || token !== this.routeToken) return;
        await this.transition(() => {
          if (token !== this.routeToken) return;
          this.status.textContent = this.dataset.error;
          this.removeAttribute('aria-busy');
          this.session.hidden = true;
          this.overview.hidden = false;
          this.hydrateCovers();
        }, animate);
      }
    }
    transition(update, animate = true) {
      if (animate && document.startViewTransition && !this.reducedMotion) {
        const transition = document.startViewTransition(update);
        transition.ready.catch(() => {});
        return transition.updateCallbackDone;
      }
      update();
      return Promise.resolve();
    }
    renderBatch() {
      const all = this.activeCollection.photos;
      const end = Math.min(this.rendered + 18, all.length);
      const fragment = document.createDocumentFragment();
      for (let index = this.rendered; index < end; index++) {
        const photo = all[index];
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'nxa__photo'; button.dataset.photo = index;
        button.setAttribute('aria-label', `${this.activeCollection.title} · ${index + 1} / ${all.length}`);
        const img = document.createElement('img');
        img.fetchPriority = index === 0 ? 'high' : 'auto';
        img.src = this.imageUrl(photo.url, Math.min(800, photo.width));
        const widths = [320,480,640,800,1200].filter(width => width <= photo.width);
        if (widths.length) img.srcset = widths.map(width => `${this.imageUrl(photo.url,width)} ${width}w`).join(', ');
        img.sizes = '(min-width: 750px) 32vw, 50vw';
        img.width = photo.width; img.height = photo.height; img.alt = photo.alt;
        img.loading = index < 3 ? 'eager' : 'lazy'; img.decoding = 'async';
        const count = document.createElement('span'); count.textContent = String(index + 1).padStart(2,'0'); count.setAttribute('aria-hidden','true');
        button.append(img,count); fragment.append(button);
      }
      this.photos.append(fragment); this.rendered = end;
      this.session.querySelector('.nxa__more').hidden = end === all.length;
      this.session.querySelector('.nxa__progress').textContent = this.dataset.shown.replace('{shown}',end).replace('{total}',all.length);
    }
    imageUrl(source,width) { const url = new URL(source); url.searchParams.set('width',width); return url.href; }
    showFull(index) {
      const photos = this.activeCollection.photos;
      this.activeIndex = (index + photos.length) % photos.length;
      const photo = photos[this.activeIndex];
      const img = this.dialog.querySelector('img'); img.src = photo.url; img.alt = photo.alt; img.width = photo.width; img.height = photo.height;
      this.dialog.querySelector('figcaption').textContent = `${this.activeCollection.title} · ${this.activeIndex + 1} / ${photos.length}`;
    }
  }
  customElements.define('nexo-archive',NexoArchive);
}

